// ----------------------------------------------------------------------------
// 🔹 AUTHENTICATION CONTEXT
// Provides authentication state and methods throughout the application
// Pure JWT / Stateless approach
// ----------------------------------------------------------------------------

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, getAccessToken, clearTokens, syncStoredUserWithToken } from '../api/auth';
import { logger } from '../utils/logger';
import { setTelemetryUser, trackEvent } from '../utils/telemetry';
import type { User, LoginRequest, RegisterRequest, AdminChurchInfo } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest, clientContext?: string) => Promise<{ adminChurches?: AdminChurchInfo[] }>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  forceLogout: (reason?: string) => void;
  updateUser: (updatedUser: User) => void;
  setUser: (user: User | null) => void;
  switchChurch: (churchId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Pull the human-readable message out of an API error body.
 *
 * The services wrap responses as `ApiResponse<T>` — `{ success, message, data,
 * errors }` — so the text lives on `message`, not `error`. Reading only `error`
 * (as this used to) fell through to a generic "check your credentials", which
 * is actively wrong for the 401 that means "you're a member, not an admin".
 * `error` is kept as a fallback because some services use that spelling.
 */
const backendMessageOf = (data: any): string | undefined =>
  data?.message || data?.error || data?.errors?.message || undefined;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Attribute page views/errors to the signed-in user + church (no-op when
  // telemetry is not configured).
  useEffect(() => {
    setTelemetryUser(user?.id ?? null, user?.churchId ?? null);
  }, [user?.id, user?.churchId]);

  // Force logout (for invalid tokens)
  const forceLogout = (reason?: string) => {
    // Store logout reason for display on login page
    if (reason) {
      localStorage.setItem('logout_reason', reason);
    }
    
    setUser(null);
    clearTokens();
    
    // Redirect to login page, preserving where the user was (e.g. a member on
    // /upload whose session expired) so login can send them back there.
    const path = window.location.pathname + window.location.search;
    const redirect =
      path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/login')
        ? `?redirect=${encodeURIComponent(path)}`
        : '';
    window.location.href = `/login${redirect}`;
  };

  // Initialize user from localStorage (check for access token)
  useEffect(() => {
    const token = getAccessToken();
    // Heal snapshots stored before token-sync existed: overlay the live
    // token's role/churchId onto the cached user before reading it.
    if (token) {
      syncStoredUserWithToken(token);
    }
    const storedUser = localStorage.getItem('auth_user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        logger.info('User session restored from localStorage');
      } catch (error) {
        logger.error('Failed to parse stored user:', error);
        clearTokens();
      }
    } else if (!token && storedUser) {
      // User data exists but no token - clear stale data
      clearTokens();
    }

    setIsLoading(false);
  }, []);

  // Listen for OAuth-triggered storage updates
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = localStorage.getItem('auth_user');
      const token = getAccessToken();
      if (storedUser && token) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          logger.info('User data updated from OAuth:', parsedUser);
        } catch (error) {
          logger.error('Failed to parse stored user:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Same-tab updates: token refresh / switch-church re-sync the cached user
    // and announce it (localStorage writes don't fire 'storage' in-tab).
    window.addEventListener('auth-user-synced', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-user-synced', handleStorageChange);
    };
  }, []);

  // Login function (tokens in response body, saved to localStorage)
  const login = async (
    credentials: LoginRequest,
    clientContext?: string,
  ): Promise<{ adminChurches?: AdminChurchInfo[] }> => {
    try {
      const response = await authApi.login(credentials, clientContext);

      const userData: User = {
        id: response.id,
        username: response.name,
        email: response.email,
        userRole: response.userRole,
        churchId: response.churchId,
        userLogo: response.userLogo,
        adminChurches: response.adminChurches,
      };

      trackEvent('login_success', {
        source: 'admin_portal',
        userRole: response.userRole,
        churchId: response.churchId ?? 'none',
      });

      // Store user data (tokens already saved by authApi.login)
      localStorage.setItem('auth_user', JSON.stringify(userData));

      setUser(userData);
      
      // Log successful authentication
      logger.info('User authenticated successfully via admin_portal');

      return { adminChurches: response.adminChurches };
    } catch (error: unknown) {
      logger.error('Login failed:', error);

      // Type guard for Axios errors
      const isAxiosError = (err: unknown): err is { response?: { status?: number; data?: any } } => {
        return typeof err === 'object' && err !== null && 'response' in err;
      };

      // Re-throw with proper structure for verification flow
      if (isAxiosError(error)) {
        const errorResponse = error.response?.data;
        if (errorResponse?.code === 'REQUIRES_VERIFICATION' && errorResponse?.sessionToken) {
          throw {
            code: 'REQUIRES_VERIFICATION',
            sessionToken: errorResponse.sessionToken,
            email: errorResponse.email,
            message: backendMessageOf(errorResponse) || 'Email verification required',
          };
        }

        // Every other failure is re-thrown with `response` INTACT. Callers
        // branch on the status + body — a non-admin signing in gets 401 with
        // `needsChurchCreation`, which Login retries in the member context to
        // reach the Song Studio. Flattening this into a plain Error (as this
        // used to) silently killed that branch and told a legitimate member
        // their credentials were wrong.
        throw error;
      }

      throw new Error(error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
    }
  };

  // Register function - Returns sessionToken for verification flow
  const register = async (data: RegisterRequest) => {
    try {
      trackEvent('signup_started', {
        source: 'admin_portal',
        emailDomain: data.email.split('@')[1] ?? 'unknown',
      });

      const response = await authApi.register(data);
      
      trackEvent('signup_verification_sent', {
        source: 'admin_portal',
        emailDomain: data.email.split('@')[1] ?? 'unknown',
      });
      
      // New API returns { data: { sessionToken, message } }
      // This should trigger verification flow, not set user
      throw {
        code: 'REQUIRES_VERIFICATION',
        sessionToken: response.data.sessionToken,
        email: data.email,
        message: response.data.message || 'Please verify your email to continue',
      };
    } catch (error: unknown) {
      logger.error('Registration failed:', error);
      
      // Type guard for custom verification error
      const isVerificationError = (err: unknown): err is { code: string; sessionToken?: string } => {
        return typeof err === 'object' && err !== null && 'code' in err;
      };
      
      // Type guard for Axios errors
      const isAxiosError = (err: unknown): err is { response?: { data?: any } } => {
        return typeof err === 'object' && err !== null && 'response' in err;
      };
      
      // If it's already a verification requirement, re-throw it
      if (isVerificationError(error) && error.code === 'REQUIRES_VERIFICATION') {
        throw error;
      }
      
      // Handle other errors
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Registration failed. Please try again.');
      }
      
      throw new Error(error instanceof Error ? error.message : 'Registration failed. Please try again.');
    }
  };

  // Logout function (clears tokens from localStorage)
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      logger.error('Logout API call failed:', error);
    } finally {
      // Clear state and storage (tokens already cleared by authApi.logout)
      setUser(null);
      localStorage.removeItem('auth_user');
    }
  };

  // Update user data (for profile updates)
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
  };

  // Switch active church (for multi-church admins). Calls the server to mint a
  // fresh JWT bound to the target church's role — without this, the role claim
  // stays bound to the original church and downstream services 403 (or worse,
  // grant elevated permissions on a church the user is only a Member of).
  const switchChurch = async (churchId: string) => {
    if (!user) return;
    try {
      await authApi.switchChurch(churchId); // saves new tokens + syncs cached user's role claim
      // saveTokens already overlaid the new token's role/churchId onto
      // auth_user; re-read it so React state matches (role may have changed
      // when switching into a church where the user isn't an Admin).
      let base = user;
      try {
        const stored = localStorage.getItem('auth_user');
        if (stored) base = JSON.parse(stored);
      } catch { /* fall back to in-memory user */ }
      const updatedUser: User = { ...base, churchId };
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      logger.info('Switched active church to:', churchId);
    } catch (error) {
      logger.error('Failed to switch church:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    forceLogout,
    updateUser,
    setUser,
    switchChurch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
